import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { WalletReadyState } from '@solana/wallet-adapter-base'
import type { WalletName } from '@solana/wallet-adapter-base'
import {
  X,
  ScrollText,
  Shield,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  FileText,
  AlertCircle,
  Wallet,
  ChevronRight,
  Download,
  Loader2,
  ArrowLeft,
} from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * The two views within the unified modal:
 * - 'terms': Shows the Terms of Service agreement
 * - 'wallets': Shows the list of available wallets to connect
 */
type ModalStep = 'terms' | 'wallets'

/**
 * Props for the ConnectWalletModal component
 * Controls modal visibility, callbacks, and initial step
 */
interface ConnectWalletModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback when terms are accepted (persists acceptance to localStorage) */
  onAcceptTerms: () => void
  /** Callback when modal is closed/declined (without accepting) */
  onClose: () => void
  /** Whether the user has already accepted the ToS (skip straight to wallets) */
  hasAcceptedTerms: boolean
}

/**
 * ConnectWalletModal Component
 *
 * A unified modal that combines Terms of Service acceptance and wallet selection
 * into a single popup. Replaces the previous two-step flow (ToS modal → wallet modal).
 *
 * Flow:
 * 1. If ToS not accepted → shows ToS agreement step first
 * 2. After accepting ToS (or if already accepted) → shows wallet selection list
 * 3. User clicks a wallet → selects and connects via wallet adapter
 *
 * Features:
 * - Accessible modal with focus management (WCAG 2.1 compliant)
 * - Smooth step transitions within a single modal
 * - Scrollable ToS content
 * - Categorized wallet list (Installed vs. Not Detected)
 * - Mobile-first responsive design with proper z-index layering
 * - 44x44px minimum touch targets for mobile (WCAG 2.5.5)
 * - Focus trap and keyboard navigation
 * - Escape key and backdrop click to close
 */
export function ConnectWalletModal({
  isOpen,
  onAcceptTerms,
  onClose,
  hasAcceptedTerms,
}: ConnectWalletModalProps): JSX.Element | null {
  // Determine the initial step based on whether terms are already accepted
  const [step, setStep] = useState<ModalStep>(hasAcceptedTerms ? 'wallets' : 'terms')
  // Checkbox state for the ToS agreement
  const [isChecked, setIsChecked] = useState(false)
  // Track if we're in the middle of selecting a wallet (for loading state)
  const [selectingWallet, setSelectingWallet] = useState<string | null>(null)

  // Refs for focus management and accessibility
  const modalRef = useRef<HTMLDivElement>(null)
  const checkboxRef = useRef<HTMLInputElement>(null)

  // Wallet adapter hooks
  const { wallets, select, connecting, connected } = useWallet()

  /**
   * Categorize wallets into installed/loadable and not-detected groups.
   * Installed wallets appear first since they can be connected immediately.
   * Loadable wallets (e.g., mobile adapters) are also shown in the primary group.
   */
  const [installedWallets, otherWallets] = useMemo(() => {
    const installed = wallets.filter(
      (w) =>
        w.readyState === WalletReadyState.Installed ||
        w.readyState === WalletReadyState.Loadable
    )
    const other = wallets.filter(
      (w) => w.readyState === WalletReadyState.NotDetected
    )
    return [installed, other]
  }, [wallets])

  /**
   * Reset step when modal opens so it always starts at the correct view.
   * If terms are accepted, go straight to wallets; otherwise show terms first.
   */
  useEffect(() => {
    if (isOpen) {
      setStep(hasAcceptedTerms ? 'wallets' : 'terms')
      setIsChecked(false)
      setSelectingWallet(null)
    }
  }, [isOpen, hasAcceptedTerms])

  /**
   * Close modal automatically when wallet connection succeeds.
   * This prevents the modal from lingering after the user connects.
   */
  useEffect(() => {
    if (connected && isOpen && step === 'wallets') {
      // Small delay to let the connection state settle
      const timer = setTimeout(() => {
        onClose()
      }, 300)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [connected, isOpen, step, onClose])

  /**
   * Handle escape key to close modal and manage body scroll lock.
   * Prevents background scrolling when modal is open (especially important on iOS Safari).
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Lock body scroll to prevent background scrolling on mobile
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
      // iOS Safari bounce-scroll prevention
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = `-${window.scrollY}px`

      // Focus the first interactive element based on the current step
      setTimeout(() => {
        if (step === 'terms') {
          checkboxRef.current?.focus()
        } else {
          // Focus the modal container for wallet step
          modalRef.current?.focus()
        }
      }, 100)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      // Restore body scroll and position
      const scrollY = document.body.style.top
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.top = ''
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
      }
    }
  }, [isOpen, onClose, step])

  /**
   * Focus trap: keeps keyboard navigation within the modal.
   * Wraps focus from last to first element and vice versa on Tab/Shift+Tab.
   */
  useEffect(() => {
    if (!isOpen) return undefined

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [isOpen])

  /**
   * Handle "Accept & Connect" button click in the ToS step.
   * Accepts the terms, then transitions to the wallet selection step.
   */
  const handleAcceptTerms = useCallback(() => {
    if (!isChecked) return
    onAcceptTerms()
    // Transition to wallet selection step within the same modal
    setStep('wallets')
  }, [isChecked, onAcceptTerms])

  /**
   * Handle wallet selection from the wallet list.
   * Calls the wallet adapter's select() to pick the wallet.
   * With autoConnect enabled in WalletProvider, this triggers connection.
   */
  const handleSelectWallet = useCallback(
    (walletName: WalletName) => {
      setSelectingWallet(walletName as string)
      try {
        select(walletName)
      } catch (error) {
        console.error('[ConnectWalletModal] Error selecting wallet:', error)
        setSelectingWallet(null)
      }
    },
    [select]
  )

  /**
   * Handle backdrop click — close the modal when clicking outside content area.
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  /**
   * Handle checkbox change for the ToS agreement.
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked)
  }

  // Don't render anything when the modal is closed
  if (!isOpen) return null

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center',
        'p-3 sm:p-4',
        // z-[10000] ensures the modal is above everything:
        // Layout header, Solana wallet adapter modal, mobile menu, toasts
        'z-[10000]'
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-wallet-modal-title"
      aria-describedby="connect-wallet-modal-description"
    >
      {/* Backdrop overlay — covers entire viewport */}
      <div
        className="absolute inset-0 bg-dark-bg/90 backdrop-blur-sm"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal content container */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className={cn(
          'relative w-full max-w-2xl overflow-hidden',
          'rounded-xl sm:rounded-2xl',
          'border border-dark-border',
          'animate-scale-in',
          'flex flex-col',
          // Mobile-optimized max height with dynamic viewport units fallback
          'max-h-[85vh] sm:max-h-[90vh]'
        )}
        style={{
          background: 'rgba(12, 16, 28, 0.98)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          boxShadow:
            '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 224, 255, 0.15)',
          maxHeight: 'min(85dvh, 85vh)',
        }}
      >
        {/* Render the appropriate step */}
        {step === 'terms' ? (
          <TermsStep
            isChecked={isChecked}
            onCheckboxChange={handleCheckboxChange}
            onAccept={handleAcceptTerms}
            onDecline={onClose}
            checkboxRef={checkboxRef}
          />
        ) : (
          <WalletSelectionStep
            installedWallets={installedWallets}
            otherWallets={otherWallets}
            onSelectWallet={handleSelectWallet}
            onClose={onClose}
            onBack={hasAcceptedTerms ? undefined : () => setStep('terms')}
            connecting={connecting}
            selectingWallet={selectingWallet}
          />
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components for each step
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Props for the Terms of Service step sub-component
 */
interface TermsStepProps {
  isChecked: boolean
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onAccept: () => void
  onDecline: () => void
  checkboxRef: React.RefObject<HTMLInputElement>
}

/**
 * TermsStep Component
 *
 * Renders the Terms of Service agreement content within the unified modal.
 * Shows key terms summary, risk acknowledgment, and a checkbox for explicit consent.
 * The "Accept & Connect" button is disabled until the checkbox is checked.
 */
function TermsStep({
  isChecked,
  onCheckboxChange,
  onAccept,
  onDecline,
  checkboxRef,
}: TermsStepProps): JSX.Element {
  return (
    <>
      {/* Header — sticky within the modal for scroll context */}
      <div
        className="sticky top-0 border-b border-dark-border p-4 sm:p-6 z-10 flex-shrink-0"
        style={{
          background: 'rgba(12, 16, 28, 0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyber-cyan/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <ScrollText
                className="w-5 h-5 sm:w-6 sm:h-6 text-cyber-cyan"
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0">
              <h2
                id="connect-wallet-modal-title"
                className="text-xl sm:text-2xl font-bold text-gradient-cyber truncate"
              >
                Terms of Service
              </h2>
              <p className="text-text-muted text-xs sm:text-sm">
                Step 1 of 2 — Please review before connecting
              </p>
            </div>
          </div>
          <button
            onClick={onDecline}
            className={cn(
              'p-2 rounded-lg flex-shrink-0',
              'border border-dark-border',
              'hover:bg-status-error/20 hover:border-status-error/50',
              'transition-colors duration-200',
              'min-w-[40px] min-h-[40px] flex items-center justify-center'
            )}
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scrollable ToS content area */}
      <div
        className="p-4 sm:p-6 space-y-5 sm:space-y-6 overflow-y-auto flex-1"
        id="connect-wallet-modal-description"
      >
        {/* Important Notice */}
        <div className="rounded-xl border-l-4 border-l-status-warning border-y border-r border-status-warning/30 p-3 sm:p-4 bg-status-warning/5">
          <div className="flex items-start space-x-3">
            <AlertTriangle
              className="w-5 h-5 text-status-warning flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <h3 className="font-bold text-status-warning mb-1 text-sm sm:text-base">
                Important Notice
              </h3>
              <p className="text-text-secondary text-xs sm:text-sm">
                By connecting your wallet, you agree to the terms and conditions
                of using CTO Pools. Please read the key points below carefully.
              </p>
            </div>
          </div>
        </div>

        {/* Key Terms Summary */}
        <div className="space-y-3 sm:space-y-4">
          <h3 className="text-base sm:text-lg font-semibold text-text-primary flex items-center space-x-2">
            <FileText
              className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-cyan"
              aria-hidden="true"
            />
            <span>Key Terms Summary</span>
          </h3>

          <div className="grid gap-2.5 sm:gap-3">
            {/* Risk Acknowledgment */}
            <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
              <div className="flex items-start space-x-3">
                <AlertCircle
                  className="w-4 h-4 sm:w-5 sm:h-5 text-status-error flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">
                    Risk Acknowledgment
                  </h4>
                  <p className="text-text-secondary text-xs sm:text-sm">
                    Cryptocurrency investments are highly speculative. You may
                    lose all deposited funds. CTO Pools is not responsible for
                    any financial losses.
                  </p>
                </div>
              </div>
            </div>

            {/* Non-Custodial Platform */}
            <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
              <div className="flex items-start space-x-3">
                <Shield
                  className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-green flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">
                    Non-Custodial Platform
                  </h4>
                  <p className="text-text-secondary text-xs sm:text-sm">
                    You retain full custody of your funds. All transactions occur
                    directly on the Solana blockchain through smart contracts.
                  </p>
                </div>
              </div>
            </div>

            {/* No Affiliation */}
            <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
              <div className="flex items-start space-x-3">
                <AlertTriangle
                  className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-yellow flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">
                    No Third-Party Affiliation
                  </h4>
                  <p className="text-text-secondary text-xs sm:text-sm">
                    CTO Pools is NOT affiliated with any token or project
                    promoted on the platform. Always do your own research (DYOR)
                    before participating.
                  </p>
                </div>
              </div>
            </div>

            {/* Wallet Security */}
            <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
              <div className="flex items-start space-x-3">
                <Shield
                  className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-purple flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">
                    Wallet Security
                  </h4>
                  <p className="text-text-secondary text-xs sm:text-sm">
                    You are solely responsible for maintaining the security of
                    your wallet and private keys. We cannot recover lost funds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Terms Link */}
        <div className="text-center">
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-cyber-cyan hover:text-cyber-cyan/80 transition-colors text-sm sm:text-base"
            onClick={(e) => e.stopPropagation()}
          >
            <span>Read Full Terms of Service</span>
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>

        {/* Checkbox Agreement — touch-friendly sizing */}
        <div className="rounded-xl border border-cyber-cyan/30 p-3 sm:p-4 bg-cyber-cyan/5">
          <label className="flex items-start space-x-3 cursor-pointer group">
            <input
              ref={checkboxRef}
              type="checkbox"
              checked={isChecked}
              onChange={onCheckboxChange}
              className={cn(
                'w-5 h-5 sm:w-5 sm:h-5 mt-0.5 rounded border-2 border-dark-border bg-dark-surface',
                'checked:bg-cyber-cyan checked:border-cyber-cyan',
                'focus:ring-2 focus:ring-cyber-cyan/50 focus:ring-offset-0',
                'transition-all duration-200 cursor-pointer',
                'flex-shrink-0'
              )}
              aria-describedby="terms-agreement-label"
            />
            <span
              id="terms-agreement-label"
              className="text-text-primary select-none group-hover:text-cyber-cyan transition-colors text-sm sm:text-base leading-relaxed"
            >
              I have read and agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyber-cyan underline hover:text-cyber-cyan/80"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </a>{' '}
              and acknowledge the risks associated with cryptocurrency
              transactions.
            </span>
          </label>
        </div>
      </div>

      {/* Footer with action buttons — sticky at bottom for mobile */}
      <div
        className="sticky bottom-0 border-t border-dark-border p-4 sm:p-6 z-10 flex-shrink-0"
        style={{
          background: 'rgba(12, 16, 28, 0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 sm:justify-end">
          <button
            onClick={onDecline}
            className={cn(
              'px-6 py-3 rounded-xl text-sm font-medium',
              'border border-dark-border text-text-secondary',
              'hover:bg-dark-panel/50 hover:text-text-primary',
              'transition-all duration-200',
              'min-h-[44px]'
            )}
            aria-label="Decline and close modal"
          >
            Decline
          </button>
          <button
            onClick={onAccept}
            disabled={!isChecked}
            className={cn(
              'px-6 py-3 rounded-xl text-sm font-medium flex items-center justify-center space-x-2',
              'border transition-all duration-200',
              'min-h-[44px]',
              isChecked
                ? 'bg-cyber-cyan/20 border-cyber-cyan text-cyber-cyan hover:bg-cyber-cyan/30 hover:shadow-neon-cyan'
                : 'bg-dark-surface/50 border-dark-border text-text-muted cursor-not-allowed opacity-50'
            )}
            aria-label={
              isChecked
                ? 'Accept terms and continue to wallet selection'
                : 'Please check the checkbox to accept terms'
            }
          >
            <CheckCircle className="w-5 h-5" aria-hidden="true" />
            <span>Accept & Connect</span>
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wallet item from the wallet adapter, typed for our list rendering
 */
interface WalletListItem {
  adapter: {
    name: WalletName
    icon: string
    url: string
  }
  readyState: WalletReadyState
}

/**
 * Props for the Wallet Selection step sub-component
 */
interface WalletSelectionStepProps {
  installedWallets: WalletListItem[]
  otherWallets: WalletListItem[]
  onSelectWallet: (walletName: WalletName) => void
  onClose: () => void
  /** Optional back arrow to return to ToS step (only shown if user hasn't previously accepted ToS) */
  onBack?: (() => void) | undefined
  connecting: boolean
  selectingWallet: string | null
}

/**
 * WalletSelectionStep Component
 *
 * Renders the wallet selection list within the unified modal.
 * Shows installed/loadable wallets first, then not-detected wallets
 * that link to their download pages.
 */
function WalletSelectionStep({
  installedWallets,
  otherWallets,
  onSelectWallet,
  onClose,
  onBack,
  connecting,
  selectingWallet,
}: WalletSelectionStepProps): JSX.Element {
  return (
    <>
      {/* Header */}
      <div
        className="sticky top-0 border-b border-dark-border p-4 sm:p-6 z-10 flex-shrink-0"
        style={{
          background: 'rgba(12, 16, 28, 0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            {/* Back button (only when navigating from ToS step) */}
            {onBack && (
              <button
                onClick={onBack}
                className={cn(
                  'p-2 rounded-lg flex-shrink-0',
                  'border border-dark-border',
                  'hover:bg-dark-panel/50 hover:border-cyber-cyan/30',
                  'transition-colors duration-200',
                  'min-w-[40px] min-h-[40px] flex items-center justify-center'
                )}
                aria-label="Go back to Terms of Service"
              >
                <ArrowLeft className="w-5 h-5 text-text-secondary" />
              </button>
            )}
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyber-cyan/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Wallet
                className="w-5 h-5 sm:w-6 sm:h-6 text-cyber-cyan"
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0">
              <h2
                id="connect-wallet-modal-title"
                className="text-xl sm:text-2xl font-bold text-gradient-cyber truncate"
              >
                Connect Wallet
              </h2>
              <p className="text-text-muted text-xs sm:text-sm">
                {onBack ? 'Step 2 of 2 — ' : ''}Choose a wallet to connect
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              'p-2 rounded-lg flex-shrink-0',
              'border border-dark-border',
              'hover:bg-status-error/20 hover:border-status-error/50',
              'transition-colors duration-200',
              'min-w-[40px] min-h-[40px] flex items-center justify-center'
            )}
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scrollable wallet list */}
      <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
        {/* Installed / Loadable wallets */}
        {installedWallets.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider px-1">
              Available Wallets
            </h3>
            <div className="grid gap-2">
              {installedWallets.map((wallet) => (
                <WalletButton
                  key={wallet.adapter.name}
                  name={wallet.adapter.name as string}
                  icon={wallet.adapter.icon}
                  onClick={() => onSelectWallet(wallet.adapter.name)}
                  isLoading={
                    (connecting && selectingWallet === wallet.adapter.name) ||
                    false
                  }
                  disabled={connecting}
                  badge="Detected"
                  badgeColor="cyber-green"
                />
              ))}
            </div>
          </div>
        )}

        {/* Not-detected wallets (with install links) */}
        {otherWallets.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider px-1">
              More Wallets
            </h3>
            <div className="grid gap-2">
              {otherWallets.map((wallet) => (
                <WalletButton
                  key={wallet.adapter.name}
                  name={wallet.adapter.name as string}
                  icon={wallet.adapter.icon}
                  onClick={() => {
                    // For not-detected wallets, open the wallet's homepage for installation
                    if (wallet.adapter.url) {
                      window.open(wallet.adapter.url, '_blank', 'noopener,noreferrer')
                    }
                  }}
                  isLoading={false}
                  disabled={connecting}
                  badge="Install"
                  badgeColor="text-muted"
                  isInstallLink
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state: no wallets detected at all */}
        {installedWallets.length === 0 && otherWallets.length === 0 && (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 mx-auto bg-dark-surface/50 rounded-2xl flex items-center justify-center">
              <Wallet className="w-8 h-8 text-text-muted" aria-hidden="true" />
            </div>
            <div>
              <p className="text-text-primary font-medium">
                No wallets found
              </p>
              <p className="text-text-secondary text-sm mt-1">
                Install a Solana wallet extension like{' '}
                <a
                  href="https://phantom.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-cyan hover:underline"
                >
                  Phantom
                </a>{' '}
                or{' '}
                <a
                  href="https://solflare.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-cyan hover:underline"
                >
                  Solflare
                </a>{' '}
                to get started.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer — cancel button */}
      <div
        className="sticky bottom-0 border-t border-dark-border p-4 sm:p-6 z-10 flex-shrink-0"
        style={{
          background: 'rgba(12, 16, 28, 0.97)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <div className="flex justify-between items-center">
          <p className="text-text-muted text-xs sm:text-sm">
            <CheckCircle className="w-3.5 h-3.5 inline-block mr-1 text-cyber-green" aria-hidden="true" />
            Terms of Service accepted
          </p>
          <button
            onClick={onClose}
            className={cn(
              'px-6 py-3 rounded-xl text-sm font-medium',
              'border border-dark-border text-text-secondary',
              'hover:bg-dark-panel/50 hover:text-text-primary',
              'transition-all duration-200',
              'min-h-[44px]'
            )}
            aria-label="Cancel and close modal"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Props for the individual wallet button in the wallet list
 */
interface WalletButtonProps {
  name: string
  icon: string
  onClick: () => void
  isLoading: boolean
  disabled: boolean
  badge: string
  badgeColor: string
  isInstallLink?: boolean
}

/**
 * WalletButton Component
 *
 * An individual wallet item in the selection list.
 * Shows the wallet icon, name, a status badge, and handles click/loading states.
 * Meets WCAG 2.5.5 minimum 44px touch target for mobile accessibility.
 */
function WalletButton({
  name,
  icon,
  onClick,
  isLoading,
  disabled,
  badge,
  badgeColor,
  isInstallLink = false,
}: WalletButtonProps): JSX.Element {
  return (
    <button
      onClick={onClick}
      disabled={disabled && !isInstallLink}
      className={cn(
        'w-full flex items-center justify-between',
        'px-4 py-3 rounded-xl',
        'border border-dark-border/50',
        'transition-all duration-200',
        // Minimum touch target height for mobile (WCAG 2.5.5)
        'min-h-[56px]',
        // Hover and interaction styles
        disabled && !isInstallLink
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-cyber-cyan/5 hover:border-cyber-cyan/30 hover:shadow-sm cursor-pointer',
        'group'
      )}
      aria-label={
        isInstallLink
          ? `Install ${name} wallet`
          : isLoading
          ? `Connecting to ${name}...`
          : `Connect with ${name}`
      }
    >
      <div className="flex items-center space-x-3">
        {/* Wallet icon */}
        <img
          src={icon}
          alt={`${name} icon`}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg"
          loading="lazy"
        />
        <div className="text-left">
          <p className="text-sm sm:text-base font-medium text-text-primary group-hover:text-cyber-cyan transition-colors">
            {name}
          </p>
          <span
            className={cn(
              'text-xs',
              badgeColor === 'cyber-green'
                ? 'text-cyber-green'
                : 'text-text-muted'
            )}
          >
            {badge}
          </span>
        </div>
      </div>

      {/* Right side: loading spinner, install icon, or chevron */}
      <div className="flex-shrink-0">
        {isLoading ? (
          <Loader2
            className="w-5 h-5 text-cyber-cyan animate-spin"
            aria-hidden="true"
          />
        ) : isInstallLink ? (
          <Download
            className="w-5 h-5 text-text-muted group-hover:text-cyber-cyan transition-colors"
            aria-hidden="true"
          />
        ) : (
          <ChevronRight
            className="w-5 h-5 text-text-muted group-hover:text-cyber-cyan transition-colors"
            aria-hidden="true"
          />
        )}
      </div>
    </button>
  )
}

export default ConnectWalletModal
